import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { VideoSource } from '../../../../shared/protocol';
import type { VideoPlayerRef, PlayerState } from '../../types/video';
import { NativeVideoPlayer } from './NativeVideoPlayer';
import { TwitchVideoPlayer } from './TwitchVideoPlayer';
import { GenericIframePlayer } from './GenericIframePlayer';

interface YouTubePlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  getIframe: () => HTMLIFrameElement;
  loadVideoById: (videoId: string, startSeconds?: number) => void;
  destroy: () => void;
}

interface YouTubeApi {
  Player: new (element: HTMLElement, options: {
    videoId: string;
    playerVars: Record<string, string | number>;
    events: { onReady: () => void; onStateChange: (event: { data: number }) => void; onError: (event: { data: number }) => void; onAutoplayBlocked: () => void };
  }) => YouTubePlayer;
}

declare global {
  interface Window { YT?: YouTubeApi; onYouTubeIframeAPIReady?: () => void; }
}

export interface VideoPlayerProps {
  source: VideoSource;
  isHost: boolean;
  onReady?: () => void;
  onStateChange?: (state: PlayerState, position: number) => void;
  onSeek?: (position: number) => void;
  onError?: (message: string) => void;
}

interface YouTubePlayerProps extends Omit<VideoPlayerProps, 'source' | 'isHost' | 'onSeek'> {
  videoId: string;
}

function mapState(value: number): PlayerState {
  return ({ '-1': 'unstarted', '0': 'ended', '1': 'playing', '2': 'paused', '3': 'buffering', '5': 'unstarted' } as Record<string, PlayerState>)[String(value)] ?? 'error';
}

function mapError(code: number): string {
  if (code === 101 || code === 150) return 'Este vídeo não permite incorporação pelo YouTube.';
  if (code === 100) return 'Este vídeo não está disponível ou foi removido.';
  return 'O YouTube não conseguiu carregar este vídeo.';
}

export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(function VideoPlayer(props, ref) {
  if (props.source.provider === 'youtube') {
    return <YouTubeVideoPlayer ref={ref} videoId={props.source.videoId} onReady={props.onReady} onStateChange={props.onStateChange} onError={props.onError} />;
  }
  if (props.source.provider === 'twitch') {
    return <TwitchVideoPlayer ref={ref} source={props.source} onReady={props.onReady} onStateChange={props.onStateChange} onSeek={props.onSeek} onError={props.onError} />;
  }
  if (props.source.provider === 'iframe') {
    return <GenericIframePlayer source={props.source} />;
  }
  return <NativeVideoPlayer ref={ref} source={props.source} onReady={props.onReady} onStateChange={props.onStateChange} onSeek={props.onSeek} onError={props.onError} />;
});

const YouTubeVideoPlayer = forwardRef<VideoPlayerRef, YouTubePlayerProps>(function YouTubeVideoPlayer({ videoId, onReady, onStateChange, onError }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const [ready, setReady] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const createPlayer = () => {
      if (cancelled || !containerRef.current || !window.YT) return;
      playerRef.current?.destroy();
      containerRef.current.replaceChildren();
      const element = document.createElement('div');
      containerRef.current.appendChild(element);
      playerRef.current = new window.YT.Player(element, {
        videoId,
        playerVars: { playsinline: 1, rel: 0, enablejsapi: 1, origin: window.location.origin },
        events: {
          onReady: () => {
            if (cancelled) return;
            playerRef.current?.getIframe().setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
            setReady(true);
            onReady?.();
          },
          onStateChange: (event) => { if (!cancelled) onStateChange?.(mapState(event.data), playerRef.current?.getCurrentTime() ?? 0); },
          onError: (event) => { if (!cancelled) { setBlocked(false); onError?.(mapError(event.data)); } },
          onAutoplayBlocked: () => { if (!cancelled) setBlocked(true); },
        },
      });
    };

    if (window.YT) createPlayer();
    else {
      const existing = document.querySelector('script[data-youtube-api]');
      if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.dataset.youtubeApi = 'true';
        document.head.appendChild(script);
      }
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { previous?.(); createPlayer(); };
    }
    return () => { cancelled = true; playerRef.current?.destroy(); playerRef.current = null; };
  }, [videoId, onError, onReady, onStateChange]);

  useImperativeHandle(ref, () => ({
    capabilities: { canPlay: true, canPause: true, canSeek: true, canGetPosition: true, canSync: true },
    play: async () => {
      try {
        const player = playerRef.current;
        player?.playVideo();
        setBlocked(false);
        window.setTimeout(() => {
          const state = player?.getPlayerState();
          if (state !== 1 && state !== 3) setBlocked(true);
        }, 800);
      } catch { setBlocked(true); }
    },
    pause: async () => { playerRef.current?.pauseVideo(); },
    seekTo: async (seconds) => { playerRef.current?.seekTo(Math.max(0, seconds), true); },
    getCurrentTime: () => playerRef.current?.getCurrentTime() ?? 0,
    getDuration: () => playerRef.current?.getDuration() ?? 0,
    getState: () => mapState(playerRef.current?.getPlayerState() ?? -1),
    isReady: () => ready && playerRef.current !== null,
  }), [ready]);

  return (
    <div className="video-frame" aria-label="Player do YouTube">
      <div ref={containerRef} className="video-embed" />
      {blocked && <div className="player-notice"><span>O navegador bloqueou a reprodução automática.</span><button className="text-button" onClick={() => { playerRef.current?.playVideo(); setBlocked(false); }}>Ativar reprodução</button></div>}
      {!ready && <div className="player-loading">Carregando vídeo...</div>}
    </div>
  );
});
